import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { Transfer, Tree } from 'antd';

import AuthButton from 'components/base/AuthButton';
import ErrorFieldMessage from 'components/base/ErrorFieldMessage';
import FollowCouponSetSwitch from 'components/campaign/campaignCreation/FollowCouponSetSwitch';

import BasePrompt from './prompt/BasePrompt';

import { createAction } from 'utils';

import SearchIcon from 'assets/images/drop_down_filter.svg';
import nodata from 'assets/images/no_data_transfer.svg';
import loading from 'assets/images/selector_loading.gif';

import './BaseMultipleSelectorV2.scss';

export const SELECTOR_SIZE = {
  HIGHER: 'higher',
  WIDER: 'wider',
  SHORTER: 'shorter',
  NARROWER: 'narrower',
};

const getUnRepeatData = (data = []) => {
  // console.log('getUnRepeatData1:', data);
  const unRepeatData = [];
  data.forEach((item) => {
    if (!item?.pk) {
      return;
    }
    const itemAlreadyExist = unRepeatData.some(
      (unRepeatItem) => unRepeatItem?.pk === item.pk,
    );
    if (!itemAlreadyExist) {
      unRepeatData.push({ ...item, key: item.pk });
    }
  });
  // console.log('unRepeatData1:', unRepeatData);
  return unRepeatData;
};

const generateTree = (treeNodes = []) =>
  treeNodes.map(({ children, ...props }) => ({
    ...props,
    children: generateTree(children),
  }));

const groupTree = (data = [], key, customTree, canNoChildren) => {
  const treeData = [];
  data.forEach((item) => {
    const parentPK = item[key]?.pk;
    if (!parentPK) {
      if (canNoChildren) {
        treeData.push({
          ...item,
          title: item.title || item.name,
          key: item.pk,
          children: [],
        });
      }
      return;
    }
    // eslint-disable-next-line eqeqeq
    const exsitGroup = treeData.find((group) => group.pk == parentPK);
    if (exsitGroup) {
      exsitGroup.children.push({
        ...item,
        title: customTree.renderChildrenTitle
          ? customTree.renderChildrenTitle(item)
          : item.title || item.name,
        key: item.pk,
      });
      return;
    }
    treeData.push({
      ...item[key],
      key: `group-${parentPK}`,
      title: customTree.renderGroupTitle
        ? customTree.renderGroupTitle(item[key])
        : item[key].title || item[key].name,
      children: [
        {
          ...item,
          title: customTree.renderChildrenTitle
            ? customTree.renderChildrenTitle(item)
            : item.title || item.name,
          key: item.pk,
        },
      ],
    });
  });
  return treeData;
};

const removeUnExsitTarget = (source = [], target = []) => {
  const cleanedTargetData = target.filter((item) =>
    source.some((sourceItem) => sourceItem.pk === item.pk),
  );
  return cleanedTargetData;
};

export default function BaseMultipleSelectorV2({
  title,
  requires,
  disabled,
  namespace = '',
  groupKey,
  tips,
  hideTopSpace,
  searchPlaceholder = 'Search by Name',
  onFocusFunc,
  custom = {
    customItem: null,
    customTitle: null,
    customContainerStyle: null,
    customFilter: null,
  },
  size = [],
  data = {
    sourceData: [],
    targetData: [],
    targetChange: () => { },
  },
  switchInfo = {
    showSwitch: false,
    checked: false,
    checkChange: () => { },
  },
  addButton = {
    title: '',
    action: () => { },
    link: '',
    customClass: '',
  },
  error = {
    id: '',
    message: '',
    error: false,
  },
  customTree = {
    renderGroupTitle: null,
    renderChildrenTitle: null,
  },
  sortable = true,
  loadMoreAction = () => { },
  canNoChildren = false,
}) {
  const { allListLoading, selectedAllListLoading } = useSelector((state) => ({
    allListLoading: state[namespace]?.allListLoading,
    selectedAllListLoading: state[namespace]?.selectedAllListLoading,
  }));
  // console.log('errorSelec:', error);
  const history = useHistory();
  const isLoading = allListLoading || selectedAllListLoading || false;
  const sourceData = data.sourceData || []
  const targetData = data.targetData || [];

  const [sourceSelectedKeys, setSourceSelectedKeys] = useState([]);
  const [targetSelectedKeys, setTargetSelectedKeys] = useState([]);
  const [searchInfo, setSearchInfo] = useState({ left: '', right: '' });

  const [cleanedSourceData, sourceParentKeys] = useMemo(() => {
    if (isLoading) {
      return [[], []]
    }
    const unRepeatData = getUnRepeatData(sourceData)
    const parentPks = [...new Set(unRepeatData?.map(item => item?.[groupKey]?.pk || item?.pk) || [])]
    return [unRepeatData, parentPks]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, sourceData?.length]);

  const [targetKeys, targetParentKeys] = useMemo(() => {
    const cleanedTargetData = removeUnExsitTarget(cleanedSourceData, getUnRepeatData(targetData))
    if (!cleanedTargetData?.length > 0) {
      return [[], []]
    }
    const targetDataKeys = cleanedTargetData.map((item) => item.key);
    const targetDataParentKeys = cleanedTargetData.map(
      (item) => item?.[groupKey]?.pk || item?.pk,
    );
    return [targetDataKeys, targetDataParentKeys]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cleanedSourceData?.length, targetData?.length]);

  const groupedSourceData = useMemo(() => {
    return groupKey
      ? groupTree(cleanedSourceData, groupKey, customTree, canNoChildren)
      : []
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canNoChildren, customTree?.renderGroupTitle, customTree?.renderChildrenTitle, groupKey, cleanedSourceData?.length]);

  const filterOption = (inputValue, option) => {
    if (custom?.customFilter) {
      return custom.customFilter(inputValue, option);
    }
    const optionName = option?.name?.toLowerCase() || '';
    return optionName.indexOf(inputValue?.toLowerCase()) > -1;
  };

  const filterAllData = (searchValue, option) => {
    if (!searchValue) {
      return true
    }
    if (option?.[groupKey]?.name && filterOption(searchValue, option[groupKey])) {
      return true
    }
    return filterOption(searchValue, option)
  }

  const filterUngroupedData = (direction) => {
    const directionAllParentKeys = direction === 'left' ? sourceParentKeys : targetParentKeys;

    const treeData = [];
    const searchValue = searchInfo[direction];
    if (!cleanedSourceData?.length > 0 || !directionAllParentKeys?.length > 0) {
      return []
    }
    cleanedSourceData.forEach((item) => {
      const isValid = filterOption(searchValue, item) &&
        directionAllParentKeys.includes(item.pk);
      if (!isValid) {
        return
      }
      treeData.push({
        key: item.key,
        title: customTree.renderGroupTitle
          ? customTree.renderGroupTitle(item)
          : item.title || item.name,
        pk: item.pk,
        name: item.name,
      });
    })
    return treeData;
  }

  const filterGroupedData = (direction) => {
    const directionAllParentKeys = direction === 'left' ? sourceParentKeys : targetParentKeys;

    const treeData = [];
    const searchValue = searchInfo[direction];
    groupedSourceData.forEach((sourceTree) => {
      const parentFilter =
        filterOption(searchValue, sourceTree) &&
        directionAllParentKeys.includes(sourceTree.pk);
      const searchedResult = sourceTree?.children.filter((child) => {
        return (
          (parentFilter || filterOption(searchValue, child)) &&
          (direction === 'left'
            ? !targetKeys.includes(child.key)
            : targetKeys.includes(child.key))
        );
      });

      if (
        !!searchedResult?.length ||
        (canNoChildren && parentFilter && !sourceTree?.children?.length)
      ) {
        const data = {
          key: sourceTree.key,
          title: customTree.renderGroupTitle
            ? customTree.renderGroupTitle(sourceTree)
            : sourceTree.title || sourceTree.name,
          pk: sourceTree.pk,
          name: sourceTree.name,
        };
        if (!!searchedResult?.length) {
          data.children = searchedResult;
        }
        treeData.push(data);
      }
    });
    return treeData;
  };

  const leftTreeFilteredData = useMemo(() => {
    return groupKey ? filterGroupedData("left") : filterUngroupedData("left");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupKey, cleanedSourceData, groupedSourceData, searchInfo.left, targetKeys, sourceParentKeys, canNoChildren, customTree?.renderGroupTitle])

  const rightTreeFilteredData = useMemo(() => {
    return groupKey ? filterGroupedData("right") : filterUngroupedData("right");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupKey, cleanedSourceData, groupedSourceData, searchInfo.right, targetKeys, canNoChildren, customTree?.renderGroupTitle])

  const leftFilterItemPks = useMemo(() => {
    if (!searchInfo.left && searchInfo.left !== 0) {
      return sourceSelectedKeys || []
    }
    if (!leftTreeFilteredData?.length > 0) {
      return []
    }
    let itemPks = []
    if (groupKey) {
      leftTreeFilteredData.forEach(data => {
        itemPks = [
          ...itemPks,
          ...data?.children?.map(item => item?.pk) || []
        ]
      })
    } else {
      itemPks = leftTreeFilteredData?.map(item => item?.pk)
    }

    return itemPks
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupKey, searchInfo.left, sourceSelectedKeys.length, leftTreeFilteredData])

  const rightFilterItemPks = useMemo(() => {
    if (!searchInfo.right && searchInfo.right !== 0) {
      return targetSelectedKeys || []
    }
    let itemPks = []
    if (groupKey) {
      rightTreeFilteredData.forEach(data => {
        itemPks = [
          ...itemPks,
          ...data?.children?.map(item => item?.pk) || []
        ]
      })
    } else {
      itemPks = rightTreeFilteredData?.map(item => item?.pk)
    }

    return itemPks
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupKey, searchInfo.right, targetSelectedKeys.length, rightTreeFilteredData])

  const searchedSelectedKeys = useMemo(() => {
    const searchedKeys = [];
    const allSelectedKeys = [...targetSelectedKeys, ...sourceSelectedKeys];

    cleanedSourceData.forEach((source) => {
      const isInTarget = targetKeys.includes(source.pk);
      const isSearchResultItem = (
        isInTarget
          ? rightFilterItemPks
          : leftFilterItemPks
      )?.includes(
        source.pk
      )
      if (!isSearchResultItem) {
        return;
      }
      const isSelected = allSelectedKeys.indexOf(source.pk) > -1;

      if (isSelected) {
        searchedKeys.push(source.pk);
      }
    });
    return searchedKeys;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    targetKeys.length,
    leftFilterItemPks,
    rightFilterItemPks,
    sourceSelectedKeys.length,
    targetSelectedKeys.length,
  ]);

  const targetChange = (newTargetKeys, direction, moveKeys) => {
    let keys = [...newTargetKeys];

    if (sortable) {
      keys.sort((a, b) => a - b);
    }
    const newTargetData = keys.map((key) =>
      // eslint-disable-next-line eqeqeq
      cleanedSourceData.find((item) => item.pk == key),
    );
    data.targetChange(newTargetData);
    console.log(
      '207: ',
      newTargetKeys,
      targetKeys,
      keys,
      data.targetData,
      targetSelectedKeys,
    );
  };


  const onSelectItem = (key, direction, treeData) => {
    onFocusFunc && onFocusFunc();
    const selectedItemKeys =
      direction === 'left' ? sourceSelectedKeys : targetSelectedKeys;
    const groupCheckSeperatorIndex = `${key}`.indexOf('-');
    if (groupCheckSeperatorIndex > -1) {
      const groupCheckedKey = key.substring(groupCheckSeperatorIndex + 1);
      const filteredTreeObject = treeData.find(
        // eslint-disable-next-line eqeqeq
        (item) => item.pk == groupCheckedKey,
      );
      if (!filteredTreeObject || !filteredTreeObject.children?.length) {
        return;
      }
      const sourceGroupTreeObject = groupedSourceData.find(
        // eslint-disable-next-line eqeqeq
        (item) => item.pk == groupCheckedKey,
      );
      // console.log('sourceGroupTreeObject:', sourceGroupTreeObject);
      if (!sourceGroupTreeObject || !sourceGroupTreeObject.children?.length) {
        return;
      }
      const sourceChildrenKeys = sourceGroupTreeObject.children.map(
        (item) => item.key,
      );
      const treeChildrenKeys = filteredTreeObject.children.map(
        (item) => item.key,
      );
      const notAllSelected = filteredTreeObject.children.some(
        (item) => !searchedSelectedKeys.includes(item.key),
      );
      const removedOldKeyList = selectedItemKeys.filter(
        (item) => !sourceChildrenKeys.includes(item),
      );
      // console.log('removedOldKeyList:', removedOldKeyList, sourceChildrenKeys);
      if (notAllSelected) {
        removedOldKeyList.push(...treeChildrenKeys);
      }
      direction === 'left'
        ? setSourceSelectedKeys(removedOldKeyList)
        : setTargetSelectedKeys(removedOldKeyList);
      return;
    }
    const isSelected = selectedItemKeys.includes(key);
    const removedOldKeyList = selectedItemKeys.filter((item) => item !== key);
    if (!isSelected) {
      removedOldKeyList.push(key);
    }
    direction === 'left'
      ? setSourceSelectedKeys(removedOldKeyList)
      : setTargetSelectedKeys(removedOldKeyList);
  };

  return (
    <>
      {!hideTopSpace ? <div style={{ marginTop: '35px' }} /> : null}
      <div
        className={`v2-selector-multiple-container ${size.includes(SELECTOR_SIZE.SHORTER) ? 'is-shorter' : ''
          } ${size.includes(SELECTOR_SIZE.HIGHER) ? 'is-higher' : ''} ${size.includes(SELECTOR_SIZE.WIDER) ? 'is-wider' : ''
          } ${size.includes(SELECTOR_SIZE.NARROWER) ? 'is-narrower' : ''}`}
        style={custom.customContainerStyle || {}}
      >
        {custom.customTitle ? (
          custom.customTitle
        ) : (
          <div className={'v2-selector-multiple-container-title'}>
            <label>{title}</label>
            {switchInfo?.showSwitch ? (
              <div style={{ marginLeft: 'auto', marginRight: '40px' }}>
                <FollowCouponSetSwitch
                  checked={switchInfo?.checked}
                  onChange={switchInfo?.checkChange || (() => { })}
                />
              </div>
            ) : null}
          </div>
        )}
        {tips ? (
          <label
            className="second-section_description"
            style={{ marginTop: '0px' }}
          >
            {tips}
          </label>
        ) : null}
        <div
          style={{
            position: 'relative',
            paddingTop: '25px',
            marginTop: '10px',
          }}
        >
          <div className="multiple-selector-v2-search-left">
            <img alt="" src={SearchIcon} />
          </div>
          <div className="multiple-selector-v2-search-right">
            <img alt="" src={SearchIcon} />
          </div>
          <Transfer
            dataSource={cleanedSourceData}
            className={`v2-selector-multiple-transfer ${error?.error ? 'v2-selector-multiple-transfer-error' : null
              }`}
            disabled={disabled}
            filterOption={filterAllData}
            showSearch
            targetKeys={targetKeys}
            onSelectChange={(sourceSelectedKeys, targetSelectedKeys) => {
              onFocusFunc && onFocusFunc();

              setSourceSelectedKeys(sourceSelectedKeys);
              setTargetSelectedKeys(targetSelectedKeys);
            }}
            onSearch={(direction, value) => {
              setSearchInfo({ ...searchInfo, [direction]: value });
            }}
            onScroll={(direction, event) => {
              if (direction === 'right') {
                return;
              }
              if (
                event.nativeEvent.target.scrollTop +
                event.nativeEvent.target.clientHeight ===
                event.nativeEvent.target.scrollHeight
              ) {
                loadMoreAction();
              }
            }}
            selectedKeys={disabled ? [] : searchedSelectedKeys}
            onChange={targetChange}
            locale={{
              searchPlaceholder: searchPlaceholder,
              selectAll: 'Select all',
              selectInvert: 'Invert selection',
              notFoundContent: (
                <img
                  alt=""
                  style={isLoading ? { width: '80px', height: '80px' } : {}}
                  src={isLoading ? loading : nodata}
                />
              ),
            }}
            render={(item) =>
              custom.customItem ? custom.customItem(item) : item?.name
            }
            titles={['Available item(s)', 'Selected item(s)']}
          >
            {groupKey
              ? ({ direction, selectedKeys }) => {
                const treeData = direction === "right" ? rightTreeFilteredData : leftTreeFilteredData;
                if (!treeData?.length) {
                  return null;
                }
                return (
                  <Tree
                    checkable
                    disabled={disabled}
                    defaultExpandAll={true}
                    className={disabled ? 'tree-disabled' : ''}
                    checkedKeys={selectedKeys}
                    treeData={generateTree(treeData)}
                    onCheck={(_, { node: { key } }) =>
                      onSelectItem(key, direction, treeData)
                    }
                    onSelect={(_, { node: { key } }) =>
                      onSelectItem(key, direction, treeData)
                    }
                  />
                );
              }
              : null}
          </Transfer>
        </div>
        {addButton?.title ? (
          <div
            style={{ marginTop: '10px' }}
            className={`custom-add-remove-mutiple-selector-add-btn-container `}
          >
            <AuthButton
              title={addButton.title}
              requires={requires}
              disabled={disabled}
              customClass={'general-section-add-new btn-add-button-common'}
              action={() => {
                if (addButton.link) {
                  history.push(addButton.link);
                }
                if (addButton.action) {
                  addButton.action();
                }
              }}
            />
          </div>
        ) : null}

        <ErrorFieldMessage
          id={error.id}
          error={error.error}
          message={error.message}
        />
      </div>
    </>
  );
}

export const BaseMultipleSelectorWithPrompt = ({
  promptConfig,
  loadSourceDataConfig = {
    action: "",
    filter: {},
  },
  addButton,
  ...props
}) => {
  const dispatch = useDispatch()
  const [showPrompt, setShowPrompt] = useState(false);
  const [refreshDate, setRefreshDate] = useState(new Date());

  useEffect(() => {
    if (loadSourceDataConfig?.action) {
      dispatch(createAction(loadSourceDataConfig.action)(loadSourceDataConfig?.filter))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshDate, loadSourceDataConfig.action])

  return (
    <>
      <BaseMultipleSelectorV2
        addButton={{
          ...addButton,
          action: () => {
            setShowPrompt(true);
            addButton?.action && addButton.action()
          },
        }}
        {...props}
      />
      <BasePrompt
        show={showPrompt}
        closeAction={() => setShowPrompt(false)}
        title={promptConfig?.title || ""}
        description={promptConfig?.description || ""}
        leftButton={{
          text: 'Done and refresh',
          action: () => {
            setRefreshDate(new Date());
            setShowPrompt(false);
          },
          ...promptConfig?.leftButton
        }}
        rightButton={{
          text: 'Create',
          action: () => { },
          ...promptConfig?.rightButton
        }}
      />
    </>
  )
}